import { LifetimeGift } from '../../../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RowStatus = 'valid' | 'warn' | 'error';

export interface ParsedGiftRow {
    id: string;
    // Raw CSV values (preserved for display / editing)
    rawDate: string;
    rawAmount: string;
    rawRecipientName: string;
    rawRelationship: string;
    rawRegular: string;
    rawNotes: string;
    // Resolved / editable values
    date: string;                  // YYYY-MM (empty if unresolved)
    amount: number | null;
    recipientName: string;
    recipient_type: 'spouse' | 'charity' | 'other';
    is_regular_from_income: boolean;
    description: string;
    // Validation
    status: RowStatus;
    issues: string[];
}

export interface RecurringGroup {
    recipientName: string;
    rowIds: string[];
    count: number;
    totalAmount: number;
}

// ---------------------------------------------------------------------------
// Template download
// ---------------------------------------------------------------------------

export function downloadGiftTemplate() {
    const rows = [
        ['Date', 'Amount', 'Recipient Name', 'Relationship', 'Regular Gift from Income', 'Notes'],
    ];
    const csv = rows.map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gifts_template.csv';
    a.click();
    URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Date parsing  (accepts DD/MM/YYYY, MM/YYYY, YYYY-MM, YYYY-MM-DD)
// ---------------------------------------------------------------------------

function parseDate(raw: string): string {
    const s = raw.trim();
    if (!s) return '';

    // YYYY-MM or YYYY-MM-DD (already ISO-ish)
    if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7);

    // DD/MM/YYYY or DD/MM/YY
    const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (dmy) {
        const [, , m, y] = dmy;
        const year = y.length === 2 ? (parseInt(y) > 50 ? `19${y}` : `20${y}`) : y;
        return `${year}-${m.padStart(2, '0')}`;
    }

    // MM/YYYY
    const my = s.match(/^(\d{1,2})\/(\d{4})$/);
    if (my) {
        const [, m, y] = my;
        return `${y}-${m.padStart(2, '0')}`;
    }

    return '';
}

// ---------------------------------------------------------------------------
// Relationship → recipient_type
// ---------------------------------------------------------------------------

function parseRelationship(raw: string): 'spouse' | 'charity' | 'other' {
    const s = raw.trim().toLowerCase();
    if (['spouse', 'civil partner', 'spouse/cp', 'husband', 'wife', 'partner'].some(k => s.includes(k))) return 'spouse';
    if (['charity', 'charitable', 'organisation', 'organization', 'trust'].some(k => s.includes(k))) return 'charity';
    return 'other';
}

function parseRegular(raw: string): boolean {
    return ['yes', 'y', 'true', '1'].includes(raw.trim().toLowerCase());
}

// ---------------------------------------------------------------------------
// Validate a single row
// ---------------------------------------------------------------------------

function validateRow(row: Omit<ParsedGiftRow, 'status' | 'issues'>): { status: RowStatus; issues: string[] } {
    const issues: string[] = [];

    if (!row.date) issues.push('Date unclear — please check format (DD/MM/YYYY)');
    else {
        // Must be a real date
        const [y, m] = row.date.split('-').map(Number);
        if (!y || !m || m < 1 || m > 12 || y < 1900 || y > new Date().getFullYear()) {
            issues.push('Date appears invalid');
        }
    }

    if (row.amount === null || isNaN(row.amount!) || row.amount! <= 0) {
        issues.push('Amount missing or invalid');
    }

    const status: RowStatus = issues.length === 0 ? 'valid'
        : issues.some(i => i.includes('missing') || i.includes('invalid')) ? 'error'
        : 'warn';

    return { status, issues };
}

// ---------------------------------------------------------------------------
// Parse CSV text → rows
// ---------------------------------------------------------------------------

function parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let inQuote = false;
    let current = '';
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
            if (inQuote && line[i + 1] === '"') { current += '"'; i++; }
            else inQuote = !inQuote;
        } else if (c === ',' && !inQuote) {
            result.push(current.trim());
            current = '';
        } else {
            current += c;
        }
    }
    result.push(current.trim());
    return result;
}

export function parseGiftCsv(csvText: string): ParsedGiftRow[] {
    const lines = csvText.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return [];

    // Skip header row (first row)
    const dataLines = lines.slice(1);

    return dataLines
        .filter(line => {
            // Skip rows where both date and amount columns are empty (trailing blank rows)
            const cols = parseCsvLine(line);
            return cols[0]?.trim() || cols[1]?.replace(/[^0-9.]/g, '').trim();
        })
        .map((line, i) => {
            const cols = parseCsvLine(line);
            const [c0 = '', c1 = '', c2 = '', c3 = '', c4 = '', c5 = ''] = cols;

            const date = parseDate(c0);
            const amountRaw = c1.replace(/[^0-9.]/g, '');
            const amount = amountRaw === '' ? null : parseFloat(amountRaw);
            const recipientName = c2.trim();
            const recipient_type = parseRelationship(c3);
            const is_regular_from_income = parseRegular(c4);
            const description = [c2.trim(), c5.trim()].filter(Boolean).join(' — ') || '';

            const base = { id: `import-${i}`, rawDate: c0, rawAmount: c1, rawRecipientName: c2, rawRelationship: c3, rawRegular: c4, rawNotes: c5, date, amount, recipientName, recipient_type, is_regular_from_income, description };
            const { status, issues } = validateRow(base);
            return { ...base, status, issues };
        });
}

// ---------------------------------------------------------------------------
// Parse XLSX (SheetJS — lazy import so no hard failure if not installed)
// ---------------------------------------------------------------------------

export async function parseGiftXlsx(buffer: ArrayBuffer): Promise<ParsedGiftRow[]> {
    let XLSX: any;
    try {
        XLSX = await import('xlsx');
    } catch {
        throw new Error('XLSX support requires the xlsx package. Run: npm install xlsx');
    }
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];

    // Use sheet_to_json with raw row arrays so we get actual cell values,
    // not formatted display strings (avoids £ symbols from currency-formatted cells).
    const allRows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (allRows.length < 2) return [];

    const dataRows = allRows.slice(1).filter((row: any[]) => {
        // Skip rows where both date (col 0) and amount (col 1) are empty
        const dateCell = String(row[0] ?? '').trim();
        const amountCell = String(row[1] ?? '').replace(/[^0-9.]/g, '').trim();
        return dateCell || amountCell;
    });

    // Helper: convert a cell value to a string, with special handling for Date objects
    // (Excel date cells come back as JS Date objects from sheet_to_json)
    const cellToString = (cell: any): string => {
        if (cell instanceof Date) {
            const y = cell.getFullYear();
            const m = String(cell.getMonth() + 1).padStart(2, '0');
            return `${y}-${m}`;
        }
        return String(cell ?? '').trim();
    };

    return dataRows.map((row: any[], i: number) => {
        const [c0 = '', c1 = '', c2 = '', c3 = '', c4 = '', c5 = ''] =
            row.map(cellToString);

        const date = parseDate(c0);
        // Keep only digits and decimal point — handles any currency prefix/formatting
        const amountRaw = c1.replace(/[^0-9.]/g, '');
        const amount = amountRaw === '' ? null : parseFloat(amountRaw);
        const recipientName = c2.trim();
        const recipient_type = parseRelationship(c3);
        const is_regular_from_income = parseRegular(c4);
        const description = [c2.trim(), c5.trim()].filter(Boolean).join(' — ') || '';

        const base = {
            id: `import-${i}`,
            rawDate: c0, rawAmount: c1, rawRecipientName: c2,
            rawRelationship: c3, rawRegular: c4, rawNotes: c5,
            date, amount, recipientName, recipient_type, is_regular_from_income, description,
        };
        const { status, issues } = validateRow(base);
        return { ...base, status, issues };
    });
}

// ---------------------------------------------------------------------------
// Detect recurring gift groups (3+ gifts to same recipient)
// ---------------------------------------------------------------------------

export function detectRecurringGroups(rows: ParsedGiftRow[]): RecurringGroup[] {
    const byName = new Map<string, ParsedGiftRow[]>();
    for (const row of rows) {
        const key = row.recipientName.trim().toLowerCase();
        if (!key) continue;
        if (!byName.has(key)) byName.set(key, []);
        byName.get(key)!.push(row);
    }
    const groups: RecurringGroup[] = [];
    for (const [, group] of byName) {
        if (group.length >= 3) {
            groups.push({
                recipientName: group[0].recipientName,
                rowIds: group.map(r => r.id),
                count: group.length,
                totalAmount: group.reduce((s, r) => s + (r.amount ?? 0), 0),
            });
        }
    }
    return groups;
}

// ---------------------------------------------------------------------------
// Map validated rows → LifetimeGift
// ---------------------------------------------------------------------------

export function mapToLifetimeGift(row: ParsedGiftRow): LifetimeGift {
    return {
        id: Math.random().toString(36).substr(2, 9),
        date: row.date,
        amount: row.amount ?? 0,
        recipient_type: row.recipient_type,
        is_regular_from_income: row.is_regular_from_income,
        description: row.description || undefined,
    };
}
